import React, { useState, useEffect } from 'react';
import KnowledgeBaseFiltersSelector from './KnowledgeBaseFiltersSelector';

const DocumentTemplateConfigDialog = ({ 
  isOpen, 
  onClose, 
  onTemplatesUpdated,
  clientHelperService 
}) => {
  const [templates, setTemplates] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  // Available metadata values for filters
  const [metadataValues, setMetadataValues] = useState({
    doc_types: [],
    sub_types: [],
    account_numbers: []
  });
  const [suggestingFiltersForSection, setSuggestingFiltersForSection] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    document_prompt_name: '',
    sections: []
  });

  // Load templates, prompts, and metadata values when dialog opens
  useEffect(() => {
    if (isOpen) {
      loadTemplates();
      loadPrompts();
      loadMetadataValues();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await clientHelperService.getDocumentTemplates();
      if (response.success && response.templates) {
        setTemplates(response.templates);
      }
    } catch (error) {
      setError('Failed to load templates');
      console.error('Error loading templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPrompts = async () => {
    try {
      // This will need a new service method to get prompts
      const response = await clientHelperService.getPrompts();
      if (response.success && response.prompts) {
        setPrompts(response.prompts);
      }
    } catch (error) {
      console.error('Error loading prompts:', error);
    }
  };

  const loadMetadataValues = async () => {
    try {
      const response = await clientHelperService.getKBMetadataValues();
      if (response.success) {
        setMetadataValues({
          doc_types: response.doc_types || [],
          sub_types: response.sub_types || [],
          account_numbers: response.account_numbers || []
        });
      }
    } catch (error) {
      console.error('Error loading metadata values:', error);
    }
  };

  const handleClose = () => {
    setEditingTemplate(null);
    setShowForm(false);
    setFormData({ name: '', description: '', document_prompt_name: '', sections: [] });
    setError('');
    setSuccess('');
    onClose();
  };

  const handleNewTemplate = () => {
    setEditingTemplate(null);
    setFormData({ 
      name: '', 
      description: '', 
      document_prompt_name: '',
      sections: [] 
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate(template);
    
    // Ensure all sections have required fields with defaults
    const normalizedSections = (template.sections || []).map((section, index) => ({
      order: section.order || index + 1,
      section_name: section.section_name || `Section ${section.order || index + 1}`,
      prompt_name: section.prompt_name || '',
      sub_prompt_name: section.sub_prompt_name || '',
      analysis_prompt_name: section.analysis_prompt_name || '',
      kb_filters: section.kb_filters || { doc_types: [], sub_types: [], account_numbers: [] }
    }));
    
    setFormData({
      name: template.name,
      description: template.description,
      document_prompt_name: template.document_prompt_name || '',
      sections: normalizedSections
    });
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDeleteTemplate = async (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete the template "${templateName}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await clientHelperService.deleteDocumentTemplate(templateId);
      setSuccess('Template deleted successfully');
      loadTemplates();
      onTemplatesUpdated?.();
    } catch (error) {
      setError('Failed to delete template');
      console.error('Error deleting template:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTemplate = async () => {
    if (!formData.name.trim()) {
      setError('Template name is required');
      return;
    }

    if (!formData.document_prompt_name.trim()) {
      setError('Document prompt name is required');
      return;
    }

    // Validate sections (if any exist)
    for (let i = 0; i < formData.sections.length; i++) {
      const section = formData.sections[i];
      if (!section.section_name || !section.section_name.trim()) {
        setError(`Section ${i + 1} name is required`);
        return;
      }
      if (!section.prompt_name || !section.prompt_name.trim()) {
        setError(`Section ${i + 1} prompt name is required`);
        return;
      }
      if (section.analysis_prompt_name && section.analysis_prompt_name.trim() && (!section.sub_prompt_name || !section.sub_prompt_name.trim())) {
        setError(`Section ${i + 1}: Analysis Prompt is required when Extract Prompt is set`);
        return;
      }
    }

    try {
      setLoading(true);
      
      const templateData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        document_prompt_name: formData.document_prompt_name.trim(),
        sections: formData.sections.map((section, index) => ({
          order: index + 1,
          section_name: section.section_name.trim(),
          prompt_name: section.prompt_name.trim(),
          sub_prompt_name: (section.sub_prompt_name || '').trim(),
          analysis_prompt_name: (section.analysis_prompt_name || '').trim(),
          kb_filters: section.kb_filters || { doc_types: [], sub_types: [], account_numbers: [] }
        }))
      };

      if (editingTemplate) {
        await clientHelperService.updateDocumentTemplate(editingTemplate.id, templateData);
        setSuccess('Template updated successfully');
      } else {
        await clientHelperService.createDocumentTemplate(templateData);
        setSuccess('Template created successfully');
      }

      setShowForm(false);
      loadTemplates();
      onTemplatesUpdated?.();
    } catch (error) {
      setError(editingTemplate ? 'Failed to update template' : 'Failed to create template');
      console.error('Error saving template:', error);
    } finally {
      setLoading(false);
    }
  };

  const addSection = () => {
    const sectionNumber = formData.sections.length + 1;
    const newSection = {
      order: sectionNumber,
      section_name: `Section ${sectionNumber}`,
      prompt_name: '',
      sub_prompt_name: '',
      analysis_prompt_name: '',
      kb_filters: { doc_types: [], sub_types: [], account_numbers: [] }
    };
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, newSection]
    }));
  };

  const removeSection = (index) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const updateSection = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) => 
        i === index ? { ...section, [field]: value } : section
      )
    }));
  };

  const moveSection = (index, direction) => {
    const newSections = [...formData.sections];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newSections.length) {
      [newSections[index], newSections[targetIndex]] = [newSections[targetIndex], newSections[index]];
      setFormData(prev => ({ ...prev, sections: newSections }));
    }
  };

  // Get unique prompt names for the dropdown
  const getPromptNames = () => {
    const uniqueNames = [...new Set(prompts.map(p => p.name))];
    return uniqueNames.sort();
  };

  // Get prompt object by name
  const getPromptByName = (promptName) => {
    if (!promptName) {
      return null;
    }
    return prompts.find(p => p.name === promptName);
  };

  // Check if a prompt has output_format
  const hasOutputFormat = (promptName) => {
    const prompt = getPromptByName(promptName);
    return prompt && prompt.output_format && prompt.output_format.trim() !== '';
  };

  // Get all prompts except the selected main prompt for sub-prompt selection
  const getSubPrompts = (promptName) => {
    if (!promptName) {
      return [];
    }
    
    // Return all prompts except the main prompt
    return prompts
      .filter(p => p.name !== promptName)
      .map(p => ({ name: p.name, prompt_id: p.prompt_id }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  // Get the full prompt text for a section (for LLM filter suggestion)
  const getSectionPromptText = (section) => {
    if (!section.prompt_name) return '';
    
    const prompt = getPromptByName(section.prompt_name);
    
    let fullText = '';
    
    // Include the prompt name as context
    fullText += `Prompt Name: ${section.prompt_name}\n\n`;
    
    // Include section name if available
    if (section.section_name) {
      fullText += `Section: ${section.section_name}\n\n`;
    }
    
    // Include prompt details if available
    if (prompt) {
      if (prompt.system_prompt) {
        fullText += `System: ${prompt.system_prompt}\n\n`;
      }
      if (prompt.prompt) {
        fullText += `Prompt: ${prompt.prompt}`;
      }
    }
    
    return fullText;
  };

  // Handle LLM filter suggestion for a section
  const handleSuggestFilters = async (sectionIndex) => {
    const section = formData.sections[sectionIndex];
    const promptText = getSectionPromptText(section);
    
    if (!promptText.trim()) {
      setError('Please select a prompt first');
      return null;
    }

    setSuggestingFiltersForSection(sectionIndex);
    
    try {
      const response = await clientHelperService.suggestKBFilters({
        promptText,
        availableDocTypes: metadataValues.doc_types,
        availableSubTypes: metadataValues.sub_types
      });

      if (response.success && response.suggested_filters) {
        // Update the section with suggested filters
        updateSection(sectionIndex, 'kb_filters', response.suggested_filters);
        return { reasoning: response.reasoning };
      } else {
        setError('Failed to get filter suggestions');
        return null;
      }
    } catch (error) {
      console.error('Error suggesting filters:', error);
      setError('Failed to get filter suggestions');
      return null;
    } finally {
      setSuggestingFiltersForSection(null);
    }
  };

  return (
    <div className={`modal ${isOpen ? 'show' : ''}`} style={{ display: isOpen ? 'block' : 'none' }} tabIndex="-1" role="dialog">
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              <i className="bi bi-gear me-2"></i>
              Document Template Configuration
            </h5>
            <button type="button" className="btn-close" onClick={handleClose}></button>
          </div>
          
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            {error && (
              <div className="alert alert-danger alert-dismissible" role="alert">
                {error}
                <button type="button" className="btn-close" onClick={() => setError('')}></button>
              </div>
            )}
            
            {success && (
              <div className="alert alert-success alert-dismissible" role="alert">
                {success}
                <button type="button" className="btn-close" onClick={() => setSuccess('')}></button>
              </div>
            )}

            {!showForm ? (
              // Template List View
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5>Document Templates</h5>
                  <button type="button" className="btn btn-primary" onClick={handleNewTemplate} disabled={loading}>
                    <i className="bi bi-plus-circle me-2"></i>
                    New Template
                  </button>
                </div>            {loading ? (
              <div className="text-center py-3">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="bi bi-file-earmark-text display-4 mb-3"></i>
                <p>No templates found. Create your first template to get started.</p>
              </div>
            ) : (
              <div>
                {templates.map((template) => (
                  <div key={template.id} className="card mb-3">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">{template.name}</h6>
                          <p className="text-muted mb-2">{template.description}</p>
                          {template.sections && template.sections.length > 0 ? (
                            <small className="text-muted">
                              {template.sections.length} section(s)
                            </small>
                          ) : (
                            <span className="badge bg-info">
                              <i className="bi bi-file-earmark me-1"></i>
                              SharePoint files only
                            </span>
                          )}
                        </div>
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={() => handleEditTemplate(template)}
                            disabled={loading}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => handleDeleteTemplate(template.id, template.name)}
                            disabled={loading}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          // Template Form View
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5>{editingTemplate ? 'Edit Template' : 'New Template'}</h5>
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowForm(false)}>
                <i className="bi bi-arrow-left me-2"></i>
                Back to List
              </button>
            </div>

            <form>
              <div className="mb-3">
                <label className="form-label">Template Name *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter template name..."
                  disabled={loading}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter template description..."
                  disabled={loading}
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Document Prompt Name *</label>
                <select
                  className="form-select"
                  value={formData.document_prompt_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, document_prompt_name: e.target.value }))}
                  disabled={loading}
                >
                  <option value="">Select document builder prompt...</option>
                  {getPromptNames().map((promptName) => (
                    <option key={promptName} value={promptName}>
                      {promptName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <label className="form-label">Template Sections</label>
                  <button type="button" className="btn btn-outline-primary btn-sm" onClick={addSection} disabled={loading}>
                    <i className="bi bi-plus-circle me-1"></i>
                    Add Section
                  </button>
                </div>

                <div className="alert alert-light border mb-3" role="alert" style={{ fontSize: '0.85rem' }}>
                  <i className="bi bi-lightbulb me-2 text-warning"></i>
                  <strong>How prompts work together:</strong>
                  <ul className="mb-0 mt-1" style={{ paddingLeft: '1.2rem' }}>
                    <li><strong>Prompt Name only</strong> &mdash; Best when you expect a short, focused response. The LLM answers in a single pass.</li>
                    <li><strong>Prompt + Analysis</strong> &mdash; The main prompt returns a JSON list of items, then the Analysis prompt runs on each item individually. Use this when you expect many results that each need detailed attention.</li>
                    <li><strong>Prompt + Extract + Analysis</strong> &mdash; Same as above, but each item first goes through an Extract prompt to pull out key details, then the Analysis prompt runs on those extracted details. Use this when there is a lot of raw information per item and the LLM would get overloaded processing it all at once.</li>
                  </ul>
                  <small className="text-muted d-block mt-1">Tip: The more information you expect back per item, the more you benefit from splitting the work across Extract and Analysis prompts.</small>
                </div>

                {formData.sections.length === 0 && (
                  <div className="alert alert-info" role="alert">
                    <i className="bi bi-info-circle me-2"></i>
                    <strong>No sections:</strong> This template will generate a document with only SharePoint files. 
                    Make sure to select SharePoint files when using this template.
                  </div>
                )}

                {formData.sections.map((section, index) => (
                  <div key={index} className="card mb-2">
                    <div className="card-body py-3">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Section {index + 1}</h6>
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => moveSection(index, 'up')}
                            disabled={loading || index === 0}
                          >
                            <i className="bi bi-arrow-up"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            onClick={() => moveSection(index, 'down')}
                            disabled={loading || index === formData.sections.length - 1}
                          >
                            <i className="bi bi-arrow-down"></i>
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeSection(index)}
                            disabled={loading}
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>

                      <div className="mb-2">
                        <label className="form-label">Section Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={section.section_name}
                          onChange={(e) => {
                            // Only allow alphanumeric characters and spaces (no special characters)
                            const sanitized = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
                            updateSection(index, 'section_name', sanitized);
                          }}
                          placeholder="Enter section name..."
                          disabled={loading}
                        />
                      </div>

                      <div className="row">
                        <div className="col-md-4">
                          <div>
                            <label className="form-label">Prompt Name</label>
                            <select
                              className="form-select"
                              value={section.prompt_name}
                              onChange={(e) => {
                                const newPromptName = e.target.value;
                                updateSection(index, 'prompt_name', newPromptName);
                                // Reset sub-prompt and analysis prompt when prompt changes
                                if (!hasOutputFormat(newPromptName)) {
                                  updateSection(index, 'sub_prompt_name', '');
                                } else {
                                  updateSection(index, 'sub_prompt_name', ''); // Reset to allow reselection
                                }
                                updateSection(index, 'analysis_prompt_name', '');
                              }}
                              disabled={loading}
                            >
                              <option value="">Select a prompt...</option>
                              {getPromptNames().map((promptName) => (
                                <option key={promptName} value={promptName}>
                                  {promptName}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div>
                            <label className="form-label">Extract Prompt (Optional)</label>
                            <select
                              className="form-select"
                              value={section.analysis_prompt_name}
                              onChange={(e) => {
                                updateSection(index, 'analysis_prompt_name', e.target.value);
                                // Clear analysis prompt when extract is removed
                                if (!e.target.value) {
                                  updateSection(index, 'sub_prompt_name', '');
                                }
                              }}
                              disabled={loading || !section.prompt_name}
                            >
                              <option value="">
                                {!section.prompt_name ? 
                                  "Select main prompt first" :
                                  "No extract prompt"
                                }
                              </option>
                              {section.prompt_name && getSubPrompts(section.prompt_name).map((subPrompt) => (
                                <option key={subPrompt.prompt_id} value={subPrompt.name}>
                                  {subPrompt.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-4">
                          <div>
                            <label className="form-label">
                              Analysis Prompt {section.analysis_prompt_name ? '* ' : '(Optional)'}
                            </label>
                            <select
                              className={`form-select ${section.analysis_prompt_name && !section.sub_prompt_name ? 'is-invalid' : ''}`}
                              value={section.sub_prompt_name}
                              onChange={(e) => updateSection(index, 'sub_prompt_name', e.target.value)}
                              disabled={loading || !section.prompt_name}
                            >
                              <option value="">
                                {!section.prompt_name ? 
                                  "Select main prompt first" :
                                  section.analysis_prompt_name ?
                                  "Required with extract prompt" :
                                  "Select analysis prompt..."
                                }
                              </option>
                              {section.prompt_name && getSubPrompts(section.prompt_name).map((subPrompt) => (
                                <option key={subPrompt.prompt_id} value={subPrompt.name}>
                                  {subPrompt.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Knowledge Base Filters */}
                      <div className="mt-3">
                        <KnowledgeBaseFiltersSelector
                          filters={section.kb_filters || { doc_types: [], sub_types: [], account_numbers: [] }}
                          onFiltersChange={(newFilters) => updateSection(index, 'kb_filters', newFilters)}
                          availableValues={metadataValues}
                          promptText={getSectionPromptText(section)}
                          onSuggestFilters={() => handleSuggestFilters(index)}
                          loading={suggestingFiltersForSection === index}
                          disabled={loading || !section.prompt_name}
                        />
                      </div>
                    </div>
                  </div>
                ))}


              </div>
            </form>
          </div>
        )}
          </div>
          
          <div className="modal-footer">
            {showForm ? (
              <>
                <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)} disabled={loading}>
                  Cancel
                </button>
                <button type="button" className="btn btn-primary" onClick={handleSaveTemplate} disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-circle me-2"></i>
                      {editingTemplate ? 'Update Template' : 'Create Template'}
                    </>
                  )}
                </button>
              </>
            ) : (
              <button className="btn btn-secondary" onClick={handleClose}>
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentTemplateConfigDialog;